import React from 'react';
import classnames from 'classnames';
import { compose } from 'redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { BiEventType } from '@wix/da-shared-react/pkg/biLogger';
import {
  withLogBiEvent,
  WithLogBiEventProps,
} from '@wix/da-shared-react/pkg/biLogger/hocs/withLogBiEvent';
import Menu, { MenuItem } from '@wix/da-shared-react/pkg/Menu';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import PlusIcon from '@wix/da-shared-react/pkg/Icons/Plus';
import ElementQueries from '@wix/da-shared-react/pkg/ElementQueries';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { ModuleType } from '../../../../types/modules';
import WidgetTypeCard from './WidgetTypeCard';
import { WithSectionContextProps } from '../../Sections/SectionContext';
import { ZoneType, InstallableModuleType } from '../../../../types/layout';
import { URLS } from '@wix/da-url';
import s from './AddWidget.scss';

export type InstallableModule = {
  moduleName: ModuleType;
  zone: ZoneType;
  position?: number;
};

export type InstallableModuleInfo =
  | Partial<InstallableModule & InstallableModuleType>
  | undefined;

export interface Props extends WithSectionContextProps {
  installableModules: InstallableModule[];
  installableModulesInfo: InstallableModuleInfo[];
  hasCoreMembership?: boolean;
  addModule: (zone: ZoneType, moduleType: ModuleType, position: number) => void;
  disabled?: boolean;
  className?: string;
}

export interface State {
  isMenuOpen: boolean;
}

const defaultState = {
  isMenuOpen: false,
};

const enhance = compose(
  withLogBiEvent,
  withTranslation()
);

export class AddWidget extends React.Component<
  Props & WithTranslation & WithLogBiEventProps,
  State
> {
  state = defaultState;
  t = this.props.t || (t => t);
  render() {
    const { installableModules, disabled, className, t } = this.props;
    const { isMenuOpen } = this.state;
    return (
      <ErrorBoundary hideOnFail>
        <div
          className={classnames(
            s['root'],
            disabled && s['disabled'],
            isMenuOpen && s['menu-is-open'],
            className
          )}
          data-role="add-widget"
        >
          <div className={s['line']}>
            <div
              className={s['button-with-gradient-line']}
              onClick={this.toggleMenu}
            >
              <button className={s['menu-toggle-btn']}>
                <PlusIcon className={s['plus-icon']} size={IconSize.SMALL} />
                <span>
                  {t(
                    !this.state.isMenuOpen
                      ? 'widgets.common.addWidget'
                      : 'common.close'
                  )}
                </span>
              </button>
            </div>
            {this.state.isMenuOpen && (
              <ElementQueries
                widthBreakpoints={[920]}
                className={s['menu-container']}
              >
                <Menu
                  className={s['menu']}
                  onSelect={this.onSelect}
                  onClose={this.closeMenu}
                >
                  {installableModules.map(
                    (installableModule: InstallableModule, idx: number) => {
                      return (
                        <MenuItem
                          className={s['menu-item']}
                          value={idx}
                          key={idx}
                        >
                          {this.renderWidgetTypeCard(installableModule)}
                        </MenuItem>
                      );
                    }
                  )}
                </Menu>
              </ElementQueries>
            )}
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  renderWidgetTypeCard = (installableModule: InstallableModule) => {
    const { section } = this.props;
    const { zone, moduleName } = installableModule;
    return (
      <WidgetTypeCard
        section={section}
        zone={zone}
        moduleName={moduleName}
        hasCoreMembership={this.props.hasCoreMembership}
      />
    );
  };

  toggleMenu = () => {
    if (!this.state.isMenuOpen) {
      // When opening an AddWidget menu, fire the ADD_WIDGET_CLICK BI Event
      this.props.logBiEvent &&
        this.props.logBiEvent({
          evid: BiEventType.ADD_WIDGET_CLICK,
        });
    }
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  };

  closeMenu = () => {
    this.setState({ isMenuOpen: false });
  };

  getInstallableModuleByIndex = (
    idx: number
  ): InstallableModuleInfo | undefined => ({
    ...(this.props.installableModules && this.props.installableModules[idx]),
    ...(this.props.installableModulesInfo &&
      this.props.installableModulesInfo[idx]),
  });

  onSelect = (idx: number) => {
    const {
      zone = null,
      moduleName = null,
      position = 0,
      limit = 0,
      restrict = null,
    } = this.getInstallableModuleByIndex(idx) || {};

    const coreLimit = restrict && restrict.priv === 'core' ? restrict.limit : 0;
    if (!this.props.hasCoreMembership && coreLimit > limit) {
      window.open(URLS.CORE_MEMBERSHIP, '_blank');
      return;
    }
    if (zone && moduleName) {
      this.props.addModule && this.props.addModule(zone, moduleName, position);

      // When user selects the widget, fire the ADD_WIDGET BI Event
      this.props.logBiEvent &&
        this.props.logBiEvent({
          evid: BiEventType.ADD_WIDGET,
          widgetname: moduleName,
        });
    }
    this.closeMenu();
  };
}

export default enhance(AddWidget) as React.ComponentType<Props>;
